import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";
import { GoogleLogin, useGoogleLogin } from "@react-oauth/google";

import {
  Color,
  FontStyle,
  defaultBottomPadding,
  defaultInnerStyle,
  defaultContainerStyle,
  ErrorCode,
  ContactType,
} from "../../constants";
import CheckButton from "../CheckButton";
import HeartButton from "../HeartButton";
import TempDisplay from "../TempDisplay";
import { CustomPaper, StyledTextField } from "../AutocompleteUtils";
import { debounce } from "../../utils/utils";
import * as apiService from "../../service/ApiService";
import googleIcon from "../../img/googleIcon.png";

const styles = {
  container: {
    ...defaultContainerStyle,
    px: { xs: "0.25rem", sm: 0 },
  },
  inner: {
    ...defaultInnerStyle,
    "& .MuiFormControl-root": {
      border: "none",
    },
  },
  subtitle: {
    ...FontStyle.body,
    fontSize: "1.2rem",
    textAlign: "center",
  },
  body: {
    ...FontStyle.body,
    fontSize: "0.83rem",
    textAlign: "left",
    lineHeight: "1.4",
    maxWidth: "500px",
    "& > * >a": {
      color: "#000",
      textDecoration: "none",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  label: {
    ...FontStyle.headingBody,
    fontSize: "1rem",
    // textAlign: 'center',
    lineHeight: "1.4",
    width: "100%",
    maxWidth: "500px",
  },
  search: {
    width: "100%",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "4px",
    border: "1px solid rgb(204, 204, 204)",
  },
  searchProps: {
    disableUnderline: true,
  },
  info: {
    // borderLeft: `6px solid ${Color.primary}`,
    borderRadius: "0.75rem",
    textAlign: "center",
    backgroundColor: "#fff",
    padding: "1.2rem",
  },
  error: {
    ...FontStyle.body,
    color: "red",
    fontSize: "0.75rem",
  },
  googleCustomButton: {
    textTransform: "none",
    fontFamily: '"Google Sans",arial,sans-serif',
    fontWeight: "500",
    height: "46px",
    // width: "500px",
    width: "100%",
    border: "1px solid #dadce0",
    color: "#202124",
    "&:hover": {
      border: "1px solid #d2e3fc",
    },
  },
  signupWithEmailText: {
    ...FontStyle.headingBody,
    fontSize: "1.2rem",
    // textAlign: 'center',
    lineHeight: "1.4",
    width: "100%",
    maxWidth: "500px",
    textAlign: "center",
    paddingTop: 1,
  },
  orText: {
    ...FontStyle.body,
    fontSize: "1rem",
    // px: '1rem',
    textAlign: "center",
    opacity: "0.4",
  },
  bar: {
    width: "100%",
    height: "1px",
    backgroundColor: "#000",
    opacity: "0.2",
  },
};

const Q14_Signup = ({
  selected,
  setSelected,
  setQuestionsDone,
  questionNum,
  accountType,
  options,
  setSignedUp,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [receiveEmails, setReceiveEmails] = useState(false);
  const [prebounceErrors, setErrors] = useState({});
  const errors = useDebounce(prebounceErrors, 1000);
  const googleCallback = (response) => {
    console.log("got google response", response);
    const { access_token } = response;
    if (access_token) {
      apiService.register(
        dispatch,
        {
          password: access_token,
          contactType: ContactType.google,
          accountType,
        },
        () => {
          setSignedUp(true);
        }
      );
    }
  };
  const customGoogleLogin = useGoogleLogin({
    onSuccess: googleCallback,
    onError: (e) => console.log("Login Failed", e),
  });

  useEffect(() => {
    setSelected((s) => {
      return {
        ...s,
        [questionNum]: [firstName, lastName, email, password, receiveEmails],
      };
    });
    if (firstName.length > 0 && firstName.length < 2) {
      setErrors((e) => {
        return { ...e, firstName: "First name must be at least 2 characters" };
      });
    } else {
      setErrors((e) => {
        return { ...e, firstName: "" };
      });
    }
    if (lastName.length > 0 && lastName.length < 2) {
      setErrors((e) => {
        return { ...e, lastName: "Last name must be at least 2 characters" };
      });
    } else {
      setErrors((e) => {
        return { ...e, lastName: "" };
      });
    }
    if (email.length > 0 && !email.includes("@")) {
      setErrors((e) => {
        return { ...e, email: "Email must be valid" };
      });
    } else {
      setErrors((e) => {
        return { ...e, email: "" };
      });
    }
    if (password.length > 0 && password.length < 8) {
      setErrors((e) => {
        return { ...e, password: "Password must be at least 8 characters" };
      });
    } else {
      setErrors((e) => {
        return { ...e, password: "" };
      });
    }
    if (
      firstName.length > 1 &&
      lastName.length > 1 &&
      email.length > 2 &&
      email.includes("@") &&
      password.length >= 8
    ) {
      setQuestionsDone((prev) => ({ ...prev, [questionNum]: true }));
    } else {
      setQuestionsDone((prev) => ({ ...prev, [questionNum]: false }));
    }
  }, [firstName, lastName, email, password, receiveEmails]);
  return (
    <Stack
      direction="column"
      sx={styles.container}
      alignItems="center"
      spacing={4}>
      {/* <TempDisplay selected={selected} options={options} questionNum={questionNum} /> */}
      <Stack
        direction="column"
        sx={styles.inner}
        alignItems="left"
        justifyContent="center"
        spacing={1}>
        <Box sx={styles.subtitle}>Sign up for free to start applying.</Box>

        <Box sx={{ paddingTop: 2, paddingBottom: 2 }}>
          <Button sx={styles.googleCustomButton} onClick={customGoogleLogin}>
            <img
              src={googleIcon}
              style={{ width: "18px", height: "18px", marginRight: "8px" }}
            />
            Sign up with Google
          </Button>
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}>
          <Box sx={styles.bar} />
          <Box sx={styles.orText}>or</Box>
          <Box sx={styles.bar} />
        </Stack>

        <Box sx={styles.signupWithEmailText}>
          Sign up with your email address
        </Box>

        <Box sx={styles.label}>First Name</Box>
        <StyledTextField
          InputProps={styles.searchProps}
          sx={styles.search}
          variant="standard"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        {errors[0].firstName && (
          <Box sx={styles.error}>{errors[0].firstName}</Box>
        )}
        <Box sx={styles.label}>Last Name</Box>
        <StyledTextField
          InputProps={styles.searchProps}
          sx={styles.search}
          variant="standard"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        {errors[0].lastName && (
          <Box sx={styles.error}>{errors[0].lastName}</Box>
        )}
        <Box sx={styles.label}>Email</Box>
        <StyledTextField
          InputProps={styles.searchProps}
          sx={styles.search}
          variant="standard"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {errors[0].email && <Box sx={styles.error}>{errors[0].email}</Box>}
        {auth.error === ErrorCode.emailAlreadyExists && (
          <Box sx={styles.error}>
            An account with this email already exists!
          </Box>
        )}
        <Box sx={styles.label}>Password</Box>
        <StyledTextField
          InputProps={styles.searchProps}
          sx={styles.search}
          variant="standard"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {errors[0].password && (
          <Box sx={styles.error}>{errors[0].password}</Box>
        )}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-start"
          alignItems="center"
          sx={{ paddingTop: 1, cursor: "pointer" }}
          onClick={() => setReceiveEmails(!receiveEmails)}>
          <Checkbox
            checked={receiveEmails}
            sx={{ padding: 0 }}
            size="large"
            style={{
              color: !receiveEmails ? "rgb(119, 119, 119)" : "black",
            }}
          />
          <Box sx={{ ...styles.body, textAlign: "left" }}>
            Yes, I would like to receive my matches, career opportunities, and
            other information via email.
          </Box>
        </Stack>
        <Box sx={{ paddingTop: 1 }}>
          <Stack
            direction="column"
            sx={{ ...styles.body, textAlign: "left", paddingLeft: "5px" }}
            spacing={2}>
            <Box>
              By signing up you agree to our{" "}
              <a href="/" style={{ fontWeight: "bold" }}>
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="/" style={{ fontWeight: "bold" }}>
                Terms & Conditions
              </a>
              .
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};

export default Q14_Signup;
